import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { PageHeader } from '@/components/shared/PageHeader';
import { DeletedWalletsTable } from '@/components/features/wallets/DeletedWalletsTable';
import { DeletedWalletWithdrawDialog } from '@/components/features/wallets/DeletedWalletWithdrawDialog';
import { QuickWithdrawReturnedDialog } from '@/components/features/wallets/QuickWithdrawReturnedDialog';
import { Button } from '@/components/ui/button';
import { Breadcrumbs } from '@/components/shared/Breadcrumbs';
import { ErrorState } from '@/components/shared/ErrorState';
import { SearchInput } from '@/components/shared/SearchInput';
import { DataTablePagination } from '@/components/shared/DataTablePagination';
import { useDeletedWallets, useRefreshDeletedWalletBalance, useReturnedWithdrawStatus } from '@/lib/hooks';
import { useDebounce } from '@/lib/hooks/useDebounce';
import { ROUTES } from '@/config/routes';
import type { DeletedWallet } from '@/lib/types';

type DeletedWalletSortField = 'balance' | 'last_transaction_id';

export const Route = createFileRoute('/_auth/_admin/admin/wallets/deleted')({
  component: DeletedWalletsComponent,
});

function DeletedWalletsComponent() {
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<DeletedWalletSortField>('balance');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [refreshingId, setRefreshingId] = useState<number | null>(null);
  const [withdrawWallet, setWithdrawWallet] = useState<DeletedWallet | null>(null);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);

  const debouncedSearch = useDebounce(search, 400);

  const { data, isLoading, isError, error } = useDeletedWallets({
    search: debouncedSearch,
    sort_by: sortBy,
    sort_order: sortOrder,
    page,
    per_page: perPage,
  });

  const refreshBalance = useRefreshDeletedWalletBalance();
  const { data: returnedStatus } = useReturnedWithdrawStatus();

  const breadcrumbs = [
    { label: 'Кошельки', href: ROUTES.ADMIN.WALLETS },
    { label: 'Заблокированные' },
  ];

  const handleWithdraw = (wallet: DeletedWallet) => {
    setWithdrawWallet(wallet);
    setWithdrawOpen(true);
  };

  const handleRefreshBalance = (id: number) => {
    setRefreshingId(id);
    refreshBalance.mutate(id, {
      onSettled: () => setRefreshingId(null),
    });
  };

  const handleSort = (field: DeletedWalletSortField) => {
    if (field === sortBy) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setPage(1);
  };

  if (isError) {
    return <ErrorState title={error.name} message={error.message} />;
  }

  return (
    <div className="space-y-6">
      <Breadcrumbs items={breadcrumbs} />

      <PageHeader
        title="Заблокированные кошельки"
        description=""
        action={
          <Button onClick={() => setQuickOpen(true)}>
            Быстрый вывод{returnedStatus?.state === 'running' ? ' (идёт...)' : ''}
          </Button>
        }
      />

      <SearchInput
        value={search}
        onChange={(value) => {
          setSearch(value);
          setPage(1);
        }}
        placeholder="Поиск по адресу"
        className="max-w-sm"
      />

      <DeletedWalletsTable
        wallets={data?.data ?? []}
        onRefreshBalance={handleRefreshBalance}
        onWithdraw={handleWithdraw}
        refreshingId={refreshingId}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        isLoading={isLoading}
      />

      {data?.meta && (
        <DataTablePagination
          meta={data.meta}
          onPageChange={setPage}
          onPerPageChange={(value) => {
            setPerPage(value);
            setPage(1);
          }}
        />
      )}

      <DeletedWalletWithdrawDialog
        open={withdrawOpen}
        onOpenChange={setWithdrawOpen}
        wallet={withdrawWallet}
      />

      <QuickWithdrawReturnedDialog open={quickOpen} onOpenChange={setQuickOpen} />
    </div>
  );
}
